import {DataQualityType, getLabelValuesAsListItem, LabelContentItem, makeFetchInitialStateForLabels } from '@tableau/data-catalog-shared';
import {
  CompactInlineAlertProps,
  CompactInlineAlertWidget,
  DropdownButtonType,
  getMaxCharValidation,
  InlineAlertType,
  List,
  ListItem,
  RadioButtonWithLabelGroupWidget,
  RadioButtonWithLabelOption,
  SingleSelectDropdown,
  TabStyles,
  TextLinkWidget,
  ToggleSwitchWidget
} from '@tableau/widgets';
import React from 'react';
import { NestedCSSProperties } from 'typestyle/lib/types';

import { ReactActionDialogBodyProps, ReactActionDialogOptions } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { cssClass, divWithStyle, OtherDivProps } from '~src/style';

import { Messages } from '../../../codegen/Localize';
import { ContentItemNameDiv, DATA_QUALITY_DIALOG_HELP_TEST_ID, DATA_QUALITY_DIALOG_MESSAGE_ID, DATA_QUALITY_DIALOG_TYPE_TEST_ID, DIALOG_TEST_ID, DIALOG_TOGGLE_TEST_ID, dropDownStyle, markupEditorStyle, MESSAGE_MAXIMUM_LENGTH, SENSITIVITY_LABEL_DIALOG_TYPE_TEST_ID } from '../../styles/LabelStyles';
import { TextFormatterOrAreaWidget } from '../common/TextFormatterOrAreaWidget';
import { canSubmitLabel } from './canSubmitLabel';
import { dataQualityTypeListItem, dataQualityTypeToString, userEditableDataQualityTypes } from './DataQualityType';
import shallowEqual = require('shallowequal');
import _ = require('underscore');

import { restApiService } from '@tableau/vizportalclient-common-components';

import { useSelector } from '~src/settings/general-settings/utils';
namespace HelpText {
  const prefix = 'help-text-div';

  const base: NestedCSSProperties = {
    paddingTop: TabStyles.Spacing.DefaultSpace,
  };

  const enabledClass = cssClass(prefix, 'enabled', base);

  const disabledClass = cssClass(prefix, 'disabled', base, {
    opacity: TabStyles.Opacity.Disabled,
  });

  export function Div({ children, disabled }: OtherDivProps & { readonly disabled?: boolean }) {
    return (
      <div className={disabled ? disabledClass : enabledClass}>{children}</div>
    );
  }
}

namespace InputHint {
  const prefix = 'input-hint-div';

  const base: NestedCSSProperties = {
    paddingTop: TabStyles.Spacing.MediumSpace,
    fontSize: TabStyles.Typography.Sizing.Default,
    color: TabStyles.Typography.Color.PrimaryLight,
  };

  const enabledClass = cssClass(prefix, 'enabled', base);

  const disabledClass = cssClass(prefix, 'disabled', base, {
    opacity: TabStyles.Opacity.Disabled,
  });

  export function Div({ children, disabled }: OtherDivProps & { readonly disabled?: boolean }) {
    return (
      <div className={disabled ? disabledClass : enabledClass}>{children}</div>
    );
  }
}

function getInitialTypeFromCategory(labelCategory: DialogType): DataQualityType {
  switch(labelCategory) {
    case 'warning':
      return DataQualityType.WARNING;
    case 'certification':
      return DataQualityType.CERTIFIED;
    case 'sensitivity':
      return DataQualityType.SENSITIVE_DATA;
  }
}

export function defaultInitialState(labelCategory: DialogType): DialogState {
  return {
    isActive: false,
    message: '',
    labelCategory: labelCategory,
    isSevere: false,
    type: dataQualityTypeToString(getInitialTypeFromCategory(labelCategory)),
  };
}

const HelpTextDiv = divWithStyle(DIALOG_TOGGLE_TEST_ID, '-help-text', {
  paddingTop: TabStyles.Spacing.SmallSpace,
  paddingBottom: TabStyles.Spacing.SmallSpace,
});

export type DialogBodyProps = {
  readonly contentItem: LabelContentItem;
  readonly loading?: boolean;
}

export type DialogState = {
  readonly luid?: string;
  readonly message: string;
  readonly isActive: boolean;
  readonly labelCategory: DialogType;
  readonly isSevere: boolean;
  readonly type: string;
}

export type ActionDialogOptions = ReactActionDialogOptions<DialogBodyProps, DialogState, unknown>;
export type DialogBodyComponentProps = ReactActionDialogBodyProps<DialogBodyProps, DialogState>;

export type DialogType = 'certification' | 'sensitivity' | 'warning';

function isFormDataValidForSubmission(args: {
  initialFormData: DialogState;
  currentFormData: DialogState;
  additionalValidation: (current: DialogState) => boolean;
}): boolean {
  const { initialFormData, currentFormData } = args;

  const formIsDirty = !shallowEqual(initialFormData, currentFormData);
  if (!formIsDirty) {
    return false;
  }

  if (!args.additionalValidation(currentFormData)) {
    return false;
  }

  return true;
}

class ContentTypeIcon {
  src: string;
  title: string;
}
  /*
  ToDo : Story 1424438
  Remove tableWithCrass from all switch statements.
  */

function matchContentTypeIcon(contentType: string){
  let contentTypeIcon = new ContentTypeIcon();
  switch(contentType){
    case 'datasource':
      contentTypeIcon.src = 'datasource_18px-large.svg?1_0_7446_6la4xqcx5my';
      contentTypeIcon.title = Messages.datasource();
      break;
    case 'view':
      contentTypeIcon.src =  'view_18px-large.svg?1_0_7446_9w7yrh46zt';
      contentTypeIcon.title = Messages.view();
      break;
    case 'virtualConnection':
      contentTypeIcon.src =  'server_connection_16px_medium.svg?20220721225838880';
      contentTypeIcon.title = Messages.publishedConnection();
      break;
    case 'database':
      contentTypeIcon.src =  'datasource_server_base_18px.svg';
      contentTypeIcon.title = Messages.database();
      break;
    case 'table':
      contentTypeIcon.src =  'datasource_table_base_18px.svg';
      contentTypeIcon.title = Messages.table();
      break;
    case 'flow':
      contentTypeIcon.src = 'flow_18px-large.svg';
      contentTypeIcon.title = Messages.flow();
      break;
    case 'virtualConnectionTable':
      contentTypeIcon.src = 'published_connection_table_medium.svg';
      contentTypeIcon.title = Messages.publishedConnectionTable();
      break;
    default:
      return null;
  }
  return contentTypeIcon;
}


const horizontalLine: React.CSSProperties = {
  border: '0.25px solid #e6e6e6',
  marginTop: '16px',
  marginBottom: '16px',
};

const iconStyle: React.CSSProperties = {
  float:'left',
  marginRight:'4px',
  width:'18px',
  height:'18px',
};


function getLabelForContentType(contentType: string): string{
  switch(contentType){
    case 'datasource':
      return Messages.datasource_lower();
    case 'view':
      return Messages.view_lower();
    case 'virtualConnection':
      return Messages.publishedConnection_lower();
    case 'database':
      return Messages.database_lower();
    case 'table':
      return Messages.table_lower();
    case 'flow':
      return Messages.flow_lower();
    case 'virtualConnectionTable':
      return Messages.publishedConnectionTableLower();
  }
}

function getPluralLabelForContentType(contentType: string): string{
  switch(contentType){
    case 'datasource':
      return Messages.datasources_lower();
    case 'view':
      return Messages.views_lower();
    case 'virtualConnection':
      return Messages.publishedConnections_lower();
    case 'database':
      return Messages.databases_lower();
    case 'table':
      return Messages.tables_lower();
    case 'flow':
      return Messages.flows_lower();
    case 'virtualConnectionTable':
      return Messages.publishedConnectionTableLower();
  }
}

const sampleInlineAlertWidgetStyles = cssClass('data-quality-dialog', 'sample-inline-alert', {
  marginTop: '8px',
  marginBottom: '8px',
});

function SampleCompactInlineAlertWidget({ type, label, hint }: Pick<CompactInlineAlertProps, 'type'> & {
  readonly label: string;
  readonly hint?: JSX.Element;
}): JSX.Element {
  return (
    <span style={{ marginTop: '4px', marginBottom: '4px' }}>
      <div style={{ marginRight: '16px' }}>{label}</div>
      <CompactInlineAlertWidget
        className={sampleInlineAlertWidgetStyles}
        type={type}
        testId={`warning-option-sample-${type}`}
        style={{ width: '180px' }}
      >{Messages.labelSampleText()}</CompactInlineAlertWidget>
      {hint && hint}
    </span>
  );
}

export function SevereWarningContent({ disabled, isSevere, setIsSevere }: {
  disabled: boolean;
  isSevere: boolean;
  setIsSevere: (b: boolean) => void;
}): JSX.Element | null {
  const buttonList: RadioButtonWithLabelOption[] = [
    {
      label: (<SampleCompactInlineAlertWidget
        label={Messages.labelStandardVisibility()}
        type={InlineAlertType.Info}
      />),
      testId: 'warning-standard-option',
      value: 'standard',
    },
    {
      label: (<SampleCompactInlineAlertWidget
        label={Messages.labelHighVisibility()}
        type={InlineAlertType.Warning}
        hint={<HelpText.Div><div >{Messages.labelHighVisibilityHelp()}</div></HelpText.Div>}
      />),
      testId: 'warning-severe-option',
      value: 'severe',
    },
  ];

  return (
    <RadioButtonWithLabelGroupWidget
      removeLeftMargin={true}
      checkedIndex={buttonList.findIndex(b => b.value === (isSevere ? 'severe' : 'standard'))}
      handleChange={!disabled ? (value) => { setIsSevere(value === 'severe'); } : undefined}
      buttonList={buttonList}
      label={<HelpText.Div>{Messages.labelSetVisibilityLevel()}</HelpText.Div>}
      style={{ width: '100%', marginBottom: '8px', opacity: disabled ? TabStyles.Opacity.Disabled : undefined }}
    />
  );
}

function getCustomValueHelpMessage(type: DialogType) {
  switch(type) {
    case 'warning':
      return Messages.dataQualityDialog_v2_Help();
    default:
      throw new Error('Not a custom value label type');
  }
}

function getCustomValueShowLabelMessage(type: DialogType) {
  switch(type) {
    case 'warning':
      return Messages.dataQualityDialog_v2_ShowWarning();
    default:
      throw new Error('Not a custom value label type');
  }
}

function WarningDialogBody({ updateDialogState, dialogState, contentItem }: DialogBodyComponentProps): React.ReactElement {
  const handleMessageChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => updateDialogState({ message: e.currentTarget.value }), [updateDialogState]);
  const { message, valid } = getMaxCharValidation(dialogState.message, MESSAGE_MAXIMUM_LENGTH);
  const disabled = !dialogState.isActive;

  return (
    <>
      <ContentItemNameDiv>{contentItem.contentName}</ContentItemNameDiv>
      <HelpText.Div>
        {getCustomValueHelpMessage(dialogState.labelCategory)}
        <div style={{ marginBottom: '16px' }}>
          <TextLinkWidget
            href={HelpService.getServerHelpUrlByCategory('dqw-manual')}
            testId={DATA_QUALITY_DIALOG_HELP_TEST_ID}
            target="_blank" rel="noopener noreferrer"
          >
            {Messages.dataQuality_learnMore()}
          </TextLinkWidget>
        </div>
        <hr style={horizontalLine} />
      </HelpText.Div>
      <ToggleSwitchWidget
        textAlign={'right'}
        onChange={(e) => updateDialogState({ isActive: e.target.checked })}
        checked={dialogState.isActive}
        children={getCustomValueShowLabelMessage(dialogState.labelCategory)}
        style={{ justifyContent: 'flex-start', marginBottom: '8px' }}
      />
      <HelpText.Div disabled={disabled}>{Messages.selectLabel()}</HelpText.Div>
      <SingleSelectDropdown
        testId={getCustomValuesTestId(dialogState.labelCategory)}
        type={DropdownButtonType.Outline}
        list={getCustomValuesTypeList(dialogState.labelCategory)}
        disabled={disabled}
        value={dialogState.type}
        onChange={(type?: string) => updateDialogState({ type })}
        style={dropDownStyle}
        label={''} />
      <SevereWarningContent
        disabled={disabled}
        isSevere={dialogState.isSevere}
        setIsSevere={(isSevere: boolean) => updateDialogState({ isSevere })}
      />

      <TextFormatterOrAreaWidget
        label={<InputHint.Div disabled={disabled}>{Messages.labelMessage()}</InputHint.Div>}
        onChange={handleMessageChange}
        id={DATA_QUALITY_DIALOG_MESSAGE_ID}
        testId={DATA_QUALITY_DIALOG_MESSAGE_ID}
        disabled={disabled}
        valid={valid}
        message={message}
        style={markupEditorStyle}
        markup={dialogState.message}
      />
    </>
  );
}

function getHelpNoteBinary(type: DialogType, contentType: string): string {
  switch(type) {
    case 'certification':
      return Messages.certificationHelpNote({
        CONTENT_TYPE: getLabelForContentType(contentType),
        CONTENT_TYPES: getPluralLabelForContentType(contentType)
      });
    case 'sensitivity':
      return Messages.sensitiveDataLabelHelpNote();
    default:
      throw Error('Label type does not exist');
  }
}

function binarySwitchMessage(type: DialogType, contentType: string): string {
  switch(type) {
    case 'certification':
      return Messages.certificationCheckboxLabel({CONTENT_TYPE: contentType});
    case 'sensitivity':
      return Messages.showSensitiveLabelMessage();
    default:
      throw Error('Label type does not exist');
  }
}

function CertificationDialogBody(props: DialogBodyComponentProps): React.ReactElement {
  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => props.updateDialogState({ message: e.currentTarget.value }),
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.updateDialogState]
  );

  const { message, valid } = getMaxCharValidation(props.dialogState.message, MESSAGE_MAXIMUM_LENGTH);
  const icon = matchContentTypeIcon(props.contentItem.contentType);

  return (
    <>
      <ContentItemNameDiv>
        {icon.src !== null ? <img src={imgPath(icon.src)} alt="" title={icon.title} style={iconStyle} ></img> : <></>}
        {props.contentItem.contentName}
      </ContentItemNameDiv>
      <HelpTextDiv>
        { getHelpNoteBinary(props.dialogState.labelCategory, props.contentItem.contentType) }
      </HelpTextDiv>
      <br/>
      <div style={{display: 'flex'}}>
        <ToggleSwitchWidget
          textAlign='right'
          testId={DIALOG_TOGGLE_TEST_ID}
          checked={props.dialogState.isActive}
          onChange={() => props.updateDialogState({ isActive: !props.dialogState.isActive })}
          children={binarySwitchMessage(props.dialogState.labelCategory, props.contentItem.contentType)}
        />
      </div>
      <TextFormatterOrAreaWidget
        label={Messages.labelMessage()}
        onChange={handleChange}
        id={DIALOG_TEST_ID}
        testId={DIALOG_TEST_ID}
        valid={valid}
        message={message}
        className={null}
        style={markupEditorStyle}
        markup={props.dialogState.message}
      />
    </>
  );
}

function SensitivityLabelDialogBody(props: DialogBodyComponentProps): React.ReactElement {
  const siteLuid = useSelector(SessionSiteSelectors.siteLuid);
  const initialLabelValues: ListItem[] | (() => ListItem[]) = [];
  const [labelValues, setLabelValues] = React.useState<Array<ListItem>>(initialLabelValues);

  React.useEffect(() => {
    let subscribed = false;
    const {makeJsonRestApiRequest} = restApiService({siteLuid});
    const fetchLabelValues = async () => {
      const values = await getLabelValuesAsListItem({category: {category: 'sensitivity'}, makeJsonRestApiRequest});
      if (!subscribed) setLabelValues(values);
    };
    fetchLabelValues().catch();

    return () => { subscribed = true; };
  }, [setLabelValues, siteLuid, ]);

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => props.updateDialogState({ message: e.currentTarget.value }),
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.updateDialogState]
  );

  const { message, valid } = getMaxCharValidation(props.dialogState.message, MESSAGE_MAXIMUM_LENGTH);
  const icon = matchContentTypeIcon(props.contentItem.contentType);
  const disabled = !FeatureFlags.sensitivityLabelsEnabled;
  const hasLabelValues = !!labelValues && labelValues.length > 0;

  return (
    <>
      <ContentItemNameDiv>
        {icon.src !== null ? <img src={imgPath(icon.src)} alt="" title={icon.title} style={iconStyle} ></img> : <></>}
        {props.contentItem.contentName}
      </ContentItemNameDiv>
      <HelpTextDiv>
        { getHelpNoteBinary(props.dialogState.labelCategory, props.contentItem.contentType) }
      </HelpTextDiv>
      <br/>
      <div style={{display: 'flex'}}>
        <ToggleSwitchWidget
          textAlign='right'
          testId={DIALOG_TOGGLE_TEST_ID}
          checked={props.dialogState.isActive}
          onChange={() => props.updateDialogState({ isActive: !props.dialogState.isActive })}
          children={binarySwitchMessage(props.dialogState.labelCategory, props.contentItem.contentType)}
        />
      </div>
      <HelpText.Div disabled={disabled}>{Messages.selectLabel()}</HelpText.Div>
      <SingleSelectDropdown
        testId={getCustomValuesTestId(props.dialogState.labelCategory)}
        type={DropdownButtonType.Outline}
        list={<List items={labelValues} />}
        disabled={disabled && hasLabelValues}
        value={props.dialogState.type}
        onChange={(type?: string) => props.updateDialogState({ type })}
        style={dropDownStyle}
        label={''} />
      <TextFormatterOrAreaWidget
        label={Messages.labelMessage()}
        onChange={handleChange}
        id={DIALOG_TEST_ID}
        testId={DIALOG_TEST_ID}
        valid={valid}
        message={message}
        className={null}
        style={markupEditorStyle}
        markup={props.dialogState.message}
      />
    </>
  );
}

  /**
 * Data Quality Service will throw if we send an enabled with a blank note
 */
function checkIsValid(current: DialogState): boolean {
  const command = canSubmitLabel({ message: current.message, active: current.isActive });
  return command === 'SUBMIT' || command === 'DELETE';
}

function fetchInitialState(labelCategory: DialogType) {
  const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());
  return makeFetchInitialStateForLabels<DialogBodyProps, DialogState>({
    category: labelCategory,
    siteLuid: siteLuid,
    transform: (t) => ({
      isActive: t.active,
      isSevere: t.elevated,
      message: t.message,
      type: t.value,
      labelCategory: labelCategory,
    }),
  });
}

const DataQualityWarningTypeListWithoutSensitiveLabelsList = () =>
<List items={userEditableDataQualityTypes.filter(type => type !== DataQualityType.CERTIFIED && type !== DataQualityType.SENSITIVE_DATA).map(dataQualityTypeListItem)} />;

const DataQualityWarningTypeList = () =>
<List items={userEditableDataQualityTypes.filter(type => type !== DataQualityType.CERTIFIED).map(dataQualityTypeListItem)} />;

function getCustomValuesTypeList(type: DialogType) {
  switch(type) {
    case 'warning':
      if(FeatureFlags.sensitivityLabelsEnabled) {
        return DataQualityWarningTypeListWithoutSensitiveLabelsList();
      } else {
        return DataQualityWarningTypeList();
      }
    default:
      throw Error('Unknown custom value label type');
  }
}

function getCustomValuesTestId(type: DialogType) {
  switch(type) {
    case 'warning':
      return DATA_QUALITY_DIALOG_TYPE_TEST_ID;
    case 'sensitivity':
      return SENSITIVITY_LABEL_DIALOG_TYPE_TEST_ID;
    default:
      throw Error('Unknown custom value label type');
  }
}


function getDialogTitle(dialogType: DialogType): string {
  switch (dialogType) {
    case 'certification':
      return Messages.certificationLabel();
    case 'sensitivity':
      return Messages.sensitivityDataLabel();
    case 'warning':
      return Messages.dataQualityDialog_Title();
    default: {
      throw Error('This Label type does not exist');
    }
  }
}

function getBodyComponent(category: DialogType) {
  switch (category) {
    case 'warning' : return WarningDialogBody;
    case 'sensitivity': return SensitivityLabelDialogBody;
    case 'certification': return CertificationDialogBody;
  }
}

export function getActionDialogOptions(args: {
  initialBodyProps: DialogBodyProps;
  initialFormData: DialogState;
  doAction: (formData: DialogState) => Promise<unknown>;

  labelCategory: DialogType;
}): ActionDialogOptions {
  const { initialBodyProps, initialFormData, doAction, labelCategory,  } = args;

  const bodyComponent = getBodyComponent(labelCategory);

  return {
    title: getDialogTitle(labelCategory),
    fetchInitialState: fetchInitialState(labelCategory),
    bodyComponent,
    initialDialogState: initialFormData,
    initialBodyProps: initialBodyProps,
    actionName: Messages.save(),
    action: (_, dialogState, __) => doAction(dialogState),
    actionDisabled: (_, dialogState, fetchedState) => !isFormDataValidForSubmission({
      initialFormData:  fetchedState ?? initialFormData,
      currentFormData: dialogState,
      additionalValidation: checkIsValid,
    }),
    showResetButtonUsingState: defaultInitialState(labelCategory),
    dismissDialogOnActionFailed: true,
    dialogWidth: TabDialogWidth.width500px,
  };
}
